import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';
import { Botao } from '../../componentes/base';
import { Carregando } from '../../componentes/estados';
import { BuscaRemota, CampoData, EntradaSensivel, Rotulo, Selecao, useCamposPersonalizados, validarCampos, AreaSensivel } from '../../componentes/formulario';
import { Drawer } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { api, ErroRequisicao, mensagemDeErro, post } from '../../nucleo/api';
import { nomeDeArquivo, nomePessoa } from '../../nucleo/formato';
import { CampoConvidados, convidadosParaApi } from './Convidados';
import { CampoReuniao, reuniaoParaApi } from './Reuniao';
import { CHAVE, diaLocal, useRecursos, useServicos } from './dados';
import { CaixaMarcar, CamposExtrasAgendamento, SeletorHorario } from '../../componentes/agendamento-comum';

export { CaixaMarcar, CamposExtrasAgendamento };
import type { Convidado, Frequencia, TipoReuniao } from './tipos';
import { useValidacaoLocal } from '../../componentes/validacao-local';

const FREQUENCIAS: Frequencia[] = ['semanal', 'quinzenal', 'mensal'];

/** Entidade dos campos personalizados do agendamento (catálogo de campos, módulo Agenda). */
export const ENTIDADE_AGENDAMENTO = 'agenda.agendamento';

/** HH:MM do relógio local do navegador (o mesmo relógio de `diaLocal`). */
function horaLocal(iso: string): string {
  const d = new Date(iso);
  const dois = (n: number) => String(n).padStart(2, '0');
  return Number.isNaN(d.getTime()) ? '' : `${dois(d.getHours())}:${dois(d.getMinutes())}`;
}

/**
 * Drawer de novo agendamento: serviço, recurso, cliente, dia, horário livre e repetição opcional. Lote 19: encaixe (hora
 * digitada sobre horário ocupado), local, link da reunião, convidados, anexos (vão ao histórico do agendamento criado) e
 * aviso ao cliente por e-mail.
 */
export function NovoAgendamento({ aberto, inicial, aoFechar, aoCriado }: { aberto: boolean; inicial: Date | null; aoFechar: () => void; aoCriado: () => void }) {
  const { t } = useTranslation('agenda');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const recursos = useRecursos();
  const servicos = useServicos();
  const extrasDefs = useCamposPersonalizados(ENTIDADE_AGENDAMENTO);

  const [servicoId, setServicoId] = useState('');
  const [recursoId, setRecursoId] = useState('');
  const [parceiroId, setParceiroId] = useState<string | null>(null);
  const [dia, setDia] = useState(diaLocal(inicial ?? new Date()));
  const [horario, setHorario] = useState('');
  const [obs, setObs] = useState('');
  const [freq, setFreq] = useState<Frequencia | ''>('');
  const [ate, setAte] = useState('');
  const [extras, setExtras] = useState<Record<string, unknown>>({});
  const [errosExtras, setErrosExtras] = useState<Record<string, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  // Lote 19 (12a a 12d).
  const [encaixe, setEncaixe] = useState(false);
  const [horaEncaixe, setHoraEncaixe] = useState('');
  const [ofereceEncaixe, setOfereceEncaixe] = useState(false);
  const [local, setLocal] = useState('');
  const [link, setLink] = useState('');
  const [tipoReuniao, setTipoReuniao] = useState<TipoReuniao>('nenhuma');
  const [convidados, setConvidados] = useState<Convidado[]>([]);
  const [arquivos, setArquivos] = useState<File[]>([]);
  const [avisarCliente, setAvisarCliente] = useState(false);
  const [avisarConvidados, setAvisarConvidados] = useState(false);
  const seletorArquivo = useRef<HTMLInputElement>(null);
  const tentado = useRef('');
  useValidacaoLocal(errosExtras, erro);

  // Cada abertura recomeça do zero, já no dia clicado no calendário.
  useEffect(() => {
    if (!aberto) return;
    setServicoId('');
    setRecursoId('');
    setParceiroId(null);
    setDia(diaLocal(inicial ?? new Date()));
    setHorario('');
    setObs('');
    setFreq('');
    setAte('');
    setExtras({});
    setErrosExtras({});
    setErro(null);
    setEncaixe(false);
    setHoraEncaixe('');
    setOfereceEncaixe(false);
    setLocal('');
    setLink('');
    setTipoReuniao('nenhuma');
    setConvidados([]);
    setArquivos([]);
    setAvisarCliente(false);
    setAvisarConvidados(false);
  }, [aberto, inicial]);

  const servico = servicos.data?.find((s) => s.id === servicoId);
  const recursosDoServico = useMemo(() => (recursos.data ?? []).filter((r) => !servico || servico.recurso_ids.length === 0 || servico.recurso_ids.includes(r.id)), [recursos.data, servico]);

  // Um recurso só: já escolhe. Recurso que o serviço não aceita: limpa.
  useEffect(() => {
    if (recursoId && !recursosDoServico.some((r) => r.id === recursoId)) setRecursoId('');
    else if (!recursoId && servicoId && recursosDoServico.length === 1) setRecursoId(recursosDoServico[0]!.id);
  }, [recursosDoServico, recursoId, servicoId]);

  const escolherHorario = useCallback((v: string) => setHorario(v), []);
  const externos = convidados.some((c) => c.tipo === 'externo');
  // 12a: no encaixe a hora é digitada (o seletor só mostra horários livres), no relógio do navegador, como o dia.
  const inicio = encaixe ? (/^\d{4}-\d{2}-\d{2}$/.test(dia) && /^\d{2}:\d{2}$/.test(horaEncaixe) ? new Date(`${dia}T${horaEncaixe}`).toISOString() : '') : horario;

  const criar = useMutation({
    mutationFn: async () => {
      tentado.current = inicio;
      const r = await post<{ id: string }>('/agenda/agendamentos', {
        recurso_id: recursoId,
        servico_id: servicoId,
        parceiro_id: parceiroId,
        inicio,
        observacoes: obs.trim() || null,
        recorrencia: freq ? { frequencia: freq, ate } : null,
        extras,
        encaixe,
        local: local.trim() || null,
        ...reuniaoParaApi(tipoReuniao, link),
        convidados: convidadosParaApi(convidados),
        avisar_cliente: avisarCliente,
        avisar_convidados: externos && avisarConvidados,
      });
      // 12c: os anexos vão para o histórico (chatter) do agendamento criado, pela rota de anexos do núcleo.
      let falhas = 0;
      for (const f of arquivos) {
        const fd = new FormData();
        fd.append('entidade', ENTIDADE_AGENDAMENTO);
        fd.append('registro_id', r.id);
        fd.append('arquivo', f);
        await api('POST', '/anexos', fd).catch(() => falhas++);
      }
      return { falhas };
    },
    onSuccess: async ({ falhas }) => {
      await qc.invalidateQueries({ queryKey: [CHAVE] });
      if (falhas) toast.erro(t('anexos.falhou'));
      else toast.sucesso(tn('comum.salvo'));
      aoCriado();
    },
    onError: async (e) => {
      if (e instanceof ErroRequisicao && e.status === 409) {
        // 12a: só agendamentos no caminho: oferece o encaixe, já com a hora tentada; bloqueio não tem encaixe.
        const possivel = e.metaDoErro()?.encaixe_possivel === true;
        setOfereceEncaixe(!encaixe && possivel);
        setErro(encaixe ? t('encaixe.bloqueado') : possivel ? t('encaixe.conflito') : t('detalhe.conflito'));
        if (!encaixe && possivel) setHoraEncaixe(horaLocal(tentado.current));
        setHorario('');
        await qc.invalidateQueries({ queryKey: [CHAVE, 'disponibilidade'] });
      } else {
        setErrosExtras(e instanceof ErroRequisicao ? e.porCampo() : {});
        setErro(mensagemDeErro(e));
      }
    },
  });

  function agendar() {
    setErro(null);
    setOfereceEncaixe(false);
    // Obrigatório dos campos personalizados é conferido antes de ir ao servidor, como no Formulario.
    const locais = validarCampos(extrasDefs, { extras });
    setErrosExtras(locais);
    if (Object.keys(locais).length) return;
    criar.mutate();
  }

  // B11: o botão desabilitado diz o que falta, em texto e ligado a ele por aria-describedby.
  const faltas = [
    !servicoId && t('campos.servico'),
    !recursoId && t('campos.recurso'),
    !parceiroId && t('campos.cliente'),
    !inicio && (encaixe ? t('campos.horaEncaixe') : t('campos.horario')),
    freq && !/^\d{4}-\d{2}-\d{2}$/.test(ate) && t('campos.ate'),
    tipoReuniao === 'link' && !link.trim() && t('campos.linkReuniao'),
  ].filter(Boolean) as string[];
  const pronto = !!servicoId && !!recursoId && !!parceiroId && !!inicio && (!freq || /^\d{4}-\d{2}-\d{2}$/.test(ate)) && (tipoReuniao !== 'link' || !!link.trim());

  return (
    <Drawer
      aberto={aberto}
      aoFechar={aoFechar}
      largura="lg"
      titulo={t('acoes.novoAgendamento')}
      rodape={
        <>
          {!pronto && (
            <p id="ag-faltam" className="mr-auto min-w-0 self-center text-xs break-words text-tinta-3">
              {t('acoes.faltam', { itens: faltas.join(', ').toLowerCase() })}
            </p>
          )}
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" disabled={!pronto} aria-describedby={pronto ? undefined : 'ag-faltam'} carregando={criar.isPending} onClick={agendar}>
            {t('acoes.agendar')}
          </Botao>
        </>
      }
    >
      {servicos.isPending || recursos.isPending ? (
        <Carregando />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-6">
          {erro && (
            <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo sm:col-span-6">
              <p className="min-w-0 grow basis-48">{erro}</p>
              {ofereceEncaixe && (
                <Botao
                  tamanho="sm"
                  onClick={() => {
                    setEncaixe(true);
                    setOfereceEncaixe(false);
                    setErro(null);
                  }}
                >
                  {t('encaixe.marcar')}
                </Botao>
              )}
            </div>
          )}
          <div className="sm:col-span-3">
            {/* Fase 2 (W18, K2): a lista é a dos serviços agendáveis do catálogo (Cadastros > Serviços). */}
            <Rotulo id="ag-servico" rotulo={t('campos.servico')} obrigatorio ajuda={servicos.data?.length ? undefined : t('vazio.servicos')}>
              <Selecao id="ag-servico" vazio={tn('comum.selecione')} opcoes={(servicos.data ?? []).map((s) => ({ valor: s.id, rotulo: `${s.nome} (${s.duracao_min} min)` }))} value={servicoId} onChange={(e) => (setServicoId(e.target.value), setHorario(''))} />
            </Rotulo>
          </div>
          <div className="sm:col-span-3">
            <Rotulo id="ag-recurso" rotulo={t('campos.recurso')} obrigatorio>
              <Selecao id="ag-recurso" vazio={tn('comum.selecione')} opcoes={recursosDoServico.map((r) => ({ valor: r.id, rotulo: `${r.tipo === 'profissional' ? nomePessoa(r.nome) : r.nome} (${t(`tipos.${r.tipo}`)})` }))} value={recursoId} onChange={(e) => (setRecursoId(e.target.value), setHorario(''))} />
            </Rotulo>
          </div>
          <div className="sm:col-span-6">
            <Rotulo id="ag-cliente" rotulo={t('campos.cliente')} obrigatorio>
              <BuscaRemota id="ag-cliente" def={{ caminho: '/cadastros/parceiros', params: { papel: 'cliente', operacao: true }, rotulo: (i) => String(i.nome) }} valor={parceiroId} aoMudar={(v) => setParceiroId(v)} />
            </Rotulo>
          </div>
          <div className="sm:col-span-2">
            <Rotulo id="ag-dia" rotulo={t('campos.data')} obrigatorio>
              <CampoData id="ag-dia" type="date" value={dia} onChange={(e) => (setDia(e.target.value), setHorario(''))} />
            </Rotulo>
          </div>
          <div className="self-end sm:col-span-4">
            <CaixaMarcar id="ag-encaixe" rotulo={t('campos.encaixe')} ajuda={t('encaixe.ajuda')} marcado={encaixe} aoMudar={(v) => (setEncaixe(v), setOfereceEncaixe(false))} />
          </div>
          {encaixe ? (
            <div className="sm:col-span-2">
              <Rotulo id="ag-hora-encaixe" rotulo={t('campos.horaEncaixe')} obrigatorio>
                <CampoData id="ag-hora-encaixe" type="time" value={horaEncaixe} onChange={(e) => setHoraEncaixe(e.target.value)} />
              </Rotulo>
            </div>
          ) : (
            <div className="sm:col-span-6">
              <p className="mb-2 text-sm font-medium text-tinta-2">
                {t('campos.horario')}
                <span className="ml-0.5 text-acento-forte">*</span>
              </p>
              <SeletorHorario servicoId={servicoId} recursoId={recursoId} dia={dia} valor={horario} aoEscolher={escolherHorario} preferido={inicial} />
            </div>
          )}
          <div className="sm:col-span-3">
            <Rotulo id="ag-rec" rotulo={t('campos.recorrencia')}>
              <Selecao
                id="ag-rec"
                opcoes={[{ valor: '', rotulo: t('recorrencia.nenhuma') }, ...FREQUENCIAS.map((f) => ({ valor: f, rotulo: t(`recorrencia.${f}`) }))]}
                value={freq}
                onChange={(e) => setFreq(e.target.value as Frequencia | '')}
              />
            </Rotulo>
          </div>
          {freq && (
            <div className="sm:col-span-3">
              <Rotulo id="ag-ate" rotulo={t('campos.ate')} obrigatorio>
                <CampoData id="ag-ate" type="date" min={dia} value={ate} onChange={(e) => setAte(e.target.value)} />
              </Rotulo>
            </div>
          )}
          <div className="sm:col-span-3">
            <Rotulo id="ag-local" rotulo={t('campos.local')}>
              <EntradaSensivel id="ag-local" mascara={null} livre maxLength={300} value={local} onChange={(e) => setLocal(e.target.value)} />
            </Rotulo>
          </div>
          {/* Lote 19 (12e): Nenhuma, Google Meet, Sala da Malgo ou Link próprio (o campo de texto). */}
          <CampoReuniao idBase="ag-reuniao" recursoId={recursoId} tipo={tipoReuniao} aoTipo={setTipoReuniao} link={link} aoLink={setLink} />
          <div className="sm:col-span-6">
            <CampoConvidados idBase="ag-conv" valor={convidados} aoMudar={setConvidados} />
          </div>
          <div className="sm:col-span-6">
            <Rotulo id="ag-obs" rotulo={t('campos.observacoes')}>
              <AreaSensivel id="ag-obs" rows={3} value={obs} onChange={(e) => setObs(e.target.value)} className="w-full min-w-0 rounded-lg border border-borda-campo bg-campo px-3 py-2 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco" />
            </Rotulo>
          </div>
          <div className="relative flex flex-col gap-2 sm:col-span-6">
            <span className="text-sm font-medium text-tinta-2">{t('campos.anexos')}</span>
            {arquivos.length > 0 && (
              <ul className="flex flex-col gap-1">
                {arquivos.map((f, i) => (
                  <li key={`${f.name}-${i}`} className="flex min-w-0 items-center gap-2 text-sm text-tinta-2">
                    <span className="min-w-0 grow truncate" title={nomeDeArquivo(f.name)}>
                      {nomeDeArquivo(f.name)}
                    </span>
                    <Botao variante="fantasma" tamanho="sm" icone={<X className="size-4" />} aria-label={t('acoes.removerItem', { nome: nomeDeArquivo(f.name) })} onClick={() => setArquivos((l) => l.filter((_, j) => j !== i))} />
                  </li>
                ))}
              </ul>
            )}
            <div className="flex flex-wrap items-center gap-3">
              <Botao icone={<Paperclip className="size-4" />} onClick={() => seletorArquivo.current?.click()}>
                {t('anexos.anexar')}
              </Botao>
              <p className="min-w-0 grow basis-48 text-xs text-tinta-3">{t('anexos.ajuda')}</p>
            </div>
            <input
              ref={seletorArquivo}
              type="file"
              multiple
              className="sr-only"
              tabIndex={-1}
              aria-label={t('campos.anexos')}
              title={t('campos.anexos')}
              onChange={(e) => {
                const lista = Array.from(e.target.files ?? []);
                setArquivos((l) => [...l, ...lista]);
                e.target.value = '';
              }}
            />
          </div>
          <div className="flex flex-col gap-3 sm:col-span-6">
            <CaixaMarcar id="ag-avisar" rotulo={t('campos.avisarCliente')} ajuda={t('aviso.ajudaCliente')} marcado={avisarCliente} aoMudar={setAvisarCliente} />
            {externos && <CaixaMarcar id="ag-avisar-conv" rotulo={t('campos.avisarConvidados')} marcado={avisarConvidados} aoMudar={setAvisarConvidados} />}
          </div>
          <CamposExtrasAgendamento defs={extrasDefs} valores={extras} erros={errosExtras} aoMudar={(k, v) => setExtras((x) => ({ ...x, [k]: v }))} />
        </div>
      )}
    </Drawer>
  );
}
